<template>
  <div v-if="inline" class="account-settings-inline"><slot /></div>
  <a-drawer
    v-else-if="compact"
    v-bind="$attrs"
    :open="open"
    :title="title"
    placement="bottom"
    :height="full ? '100%' : 'auto'"
    :closable="closable"
    :keyboard="closable && !confirmLoading"
    :mask-closable="maskClosable && !confirmLoading"
    class="account-settings-overlay"
    :class="{ 'account-settings-overlay--full': full }"
    @close="cancel"
    @after-open-change="afterOpenChange"
  >
    <template v-if="$slots.title" #title><slot name="title" /></template>
    <span ref="drawerMarker" hidden></span>
    <slot />
    <template v-if="$slots.footer || footer !== null" #footer>
      <slot name="footer"
        ><div class="account-settings-overlay__actions">
          <a-button :aria-label="cancelText" :disabled="confirmLoading" @click="cancel">{{
            cancelText
          }}</a-button>
          <a-button
            v-bind="okButtonProps"
            :aria-label="okText"
            type="primary"
            :loading="confirmLoading"
            @click="$emit('ok')"
            >{{ okText }}</a-button
          >
        </div></slot
      >
    </template>
  </a-drawer>
  <a-modal
    v-else
    v-bind="$attrs"
    :open="open"
    :title="title"
    :width="width"
    :footer="footer"
    :closable="closable"
    :keyboard="closable && !confirmLoading"
    :mask-closable="maskClosable && !confirmLoading"
    :confirm-loading="confirmLoading"
    :ok-text="okText"
    :cancel-text="cancelText"
    :ok-button-props="okButtonProps"
    @update:open="$emit('update:open', $event)"
    @cancel="$emit('cancel')"
    @ok="$emit('ok')"
    :after-close="() => $emit('after-close')"
  >
    <template v-if="$slots.title" #title><slot name="title" /></template>
    <slot />
    <template v-if="$slots.footer" #footer><slot name="footer" /></template>
  </a-modal>
</template>

<script setup lang="ts">
import type { ButtonProps } from 'ant-design-vue';
import { ref, watchEffect } from 'vue';
import { useCompactViewport } from '@/composables/useCompactViewport';
defineOptions({ inheritAttrs: false });
const props = withDefaults(
  defineProps<{
    open?: boolean;
    title?: string;
    width?: number | string;
    footer?: null;
    full?: boolean;
    inline?: boolean;
    closable?: boolean;
    maskClosable?: boolean;
    confirmLoading?: boolean;
    okText?: string;
    cancelText?: string;
    okButtonProps?: ButtonProps;
  }>(),
  {
    open: false,
    full: false,
    inline: false,
    width: 520,
    closable: true,
    maskClosable: true,
    confirmLoading: false,
    okText: '确定',
    cancelText: '取消',
  },
);
const emit = defineEmits<{
  'update:open': [value: boolean];
  cancel: [];
  ok: [];
  'after-close': [];
}>();
const compact = useCompactViewport();
const drawerMarker = ref<HTMLElement | null>(null);
watchEffect(() => {
  const panel = drawerMarker.value?.closest('.ant-drawer-content');
  if (!panel) return;
  // shortcut: 当前 Ant Drawer 不透传 ARIA 属性，组件支持后改为直接传递。
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-label', props.title || '设置窗口');
});
function cancel() {
  emit('update:open', false);
  emit('cancel');
}
function afterOpenChange(open: boolean) {
  if (!open) emit('after-close');
}
</script>

<style>
.account-settings-overlay .ant-drawer-content {
  max-height: 90dvh;
  border-radius: var(--ws-radius-lg, 16px) var(--ws-radius-lg, 16px) 0 0;
}
.account-settings-overlay--full .ant-drawer-content {
  max-height: 100%;
  border-radius: 0;
}
.account-settings-overlay .ant-drawer-header {
  padding: max(12px, env(safe-area-inset-top)) 16px 12px;
}
.account-settings-overlay .ant-drawer-header-title {
  min-width: 0;
}
.account-settings-overlay .ant-drawer-title {
  overflow-wrap: anywhere;
}
.account-settings-overlay .ant-drawer-close {
  min-width: 44px;
  min-height: 44px;
  margin: -8px 4px -8px -8px;
}
.account-settings-overlay .ant-drawer-body {
  padding: 16px;
  max-height: 70dvh;
  overflow: auto;
  overflow-wrap: anywhere;
}
.account-settings-overlay--full .ant-drawer-body {
  max-height: none;
}
.account-settings-overlay .ant-drawer-footer {
  padding: 12px 16px max(12px, env(safe-area-inset-bottom));
}
.account-settings-overlay__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.account-settings-overlay .ant-btn {
  min-height: 44px;
  height: auto;
  white-space: normal;
}
.account-settings-overlay .ant-form-item {
  margin-bottom: 20px;
}
.account-settings-overlay .ant-input-number {
  width: 100%;
}
.account-settings-overlay .form-actions {
  position: sticky;
  bottom: -16px;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 0 max(16px, env(safe-area-inset-bottom));
  background: var(--ws-color-surface, #fff);
}
.account-settings-overlay .ant-checkbox-wrapper,
.account-settings-overlay .ant-radio-wrapper {
  min-height: 44px;
  display: flex;
  align-items: center;
  margin-inline-start: 0;
}
.account-settings-overlay .form-actions .ant-btn {
  flex: 1;
}
</style>
